/**
 * The explicit route table. No file-system routing and no router library:
 * this app has a small, fixed set of surfaces, and a table you can read in
 * one screen is worth more here than a convention.
 *
 * `play` and `beat` are the only routes that need a session; everything else
 * is reachable cold.
 */
export const ROUTES = [
  "landing",
  "scenarios",
  "auth",
  "privacy",
  /** The terms of use (2026-10-22): reached cold like privacy, outside the shell, and counted as a visit like it. */
  "terms",
  "onboarding",
  "home",
  "beat",
  "play",
  "recommend",
  "debrief",
  "audit",
  "profile",
  "coach",
  "library",
  /** A finished engagement read back as a book (2026-09-24): `#/book?session=<id>`. */
  "book",
  /** The six skills, explained: a reference a learner reaches from the record, never a door in the nav. */
  "skills",
  /**
   * The two pages an email leads to (CME stage 3b, 2026-10-11): reached cold
   * from a signed link, outside the shell like `privacy`, with no identity.
   * `#/unsubscribe?token=` carries one button that withdraws the purpose the
   * token names (so a link prefetcher never unsubscribes anyone), and
   * `#/check-in?token=` carries the four questions after recruiting season.
   * Neither is counted as a visit (data/visit-count.ts).
   */
  "unsubscribe",
  "check-in",
  /**
   * The two pages an account email leads to (2026-10-22, hard verification
   * and password reset): `#/verify?token=` and `#/reset?token=`, the links
   * the api builds from better-auth's token. Outside the shell, reached cold,
   * and never counted. Like the mail pages, neither acts on arrival: the
   * verify page waits for its button, the reset page for a new password, so
   * a link scanner that opens the address confirms and changes nothing.
   */
  "verify",
  "reset",
  /**
   * The region gate's page (2026-10-28): where the api sends a browser it
   * refused at Google's callback, which would have made an account from the
   * EU, the EEA or the UK (contracts' REGION_NOT_OFFERED_PAGE_HASH). It shows
   * the same screen as a 451 from any other door. Never counted.
   */
  "not-offered",
  /** Dev-only; see app.tsx. Renders every DocumentKind x every Fidelity. */
  "gallery",
  /** Dev-only; see app.tsx. The narrative-to-scene stage (ADR 0014): write a scene, watch the section compile it. */
  "stage",
  /** Dev-only; see app.tsx. The play surface against synthetic long content, for the layout review sweep. */
  "play-review",
  /** Dev-only; see app.tsx. The front door against fixture openings, for the same review. */
  "front-door-review",
] as const;

export type RouteName = (typeof ROUTES)[number];

export function isRouteName(value: string): value is RouteName {
  return (ROUTES as readonly string[]).includes(value);
}

/**
 * The hash fragment is the whole router. Reload-safe, no server rewrite rules.
 * A query after the route (`#/play-review?turns=40`) belongs to the screen,
 * not to the table, so it is cut off before the name is matched.
 */
export function routeFromHash(hash: string): RouteName {
  const raw = (hash.replace(/^#\/?/, "").split("?")[0] ?? "").trim();
  return isRouteName(raw) ? raw : "landing";
}

export function hashForRoute(route: RouteName): string {
  return `#/${route}`;
}

/**
 * THE TWO PLACES (2026-11-02, Andre, from the Codex design session; the plan
 * is docs/DESIGN-PASS-2026-11-02-STUDY-AND-BOOK-PLAN.md). The product has two
 * states a learner can be in, and the interface says which one at a glance:
 * inside a case — THE BOOK, paper and ink, a story being told — or outside
 * one, with the coach — THE STUDY, dark and thin and lit. The place is a
 * property of the route alone: the stylesheet reads it from `data-place` on
 * the document root (design/place.ts), and every surface under that route
 * takes its tokens from it. Not "environment": that word is the stage's.
 *
 * Null is a route that belongs to neither — the landing, the sign-in card,
 * the reading pages, the pages an email leads to and the region gate carry
 * no attribute and read the root tokens, which since the shared ink and
 * paper (2026-11-03) are the book's Flax and ink; the landing's near-black
 * ground is its own.
 */
export type Place = "study" | "book";

/** The coach's pages: the front door (and the intake drawn on it), the shelf, the record, the skills page, and the front door's review bench. */
const STUDY_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>(["home", "onboarding", "library", "profile", "coach", "skills", "front-door-review"]);
/** Every book is light: an engagement in play, its endgame, a finished one read back (`book`), and the play surface's review bench. */
const BOOK_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>(["play", "beat", "recommend", "debrief", "audit", "book", "play-review"]);

export function placeOf(route: RouteName): Place | null {
  if (STUDY_ROUTES.has(route)) return "study";
  if (BOOK_ROUTES.has(route)) return "book";
  return null;
}

/**
 * The two reading pages (2026-10-22), each linked from the sign-in card's
 * notices and from the other: "back." on either returns to the address the
 * reading began from, never to the other page.
 */
export const READING_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>(["terms", "privacy"]);

/**
 * The pages the region gate's screen gives way to once the learner follows
 * one of its doors (2026-10-28): the two reading pages, signing in (for an
 * account made elsewhere, to reach its export and its deletion; the card's
 * forgotten password is the same route), and the pages an email leads to (the
 * unsubscribe and check-in links, the confirmation and reset links). Every
 * other route shows the screen again.
 */
export const REGION_OPEN_ROUTES: ReadonlySet<RouteName> = new Set<RouteName>(["terms", "privacy", "auth", "verify", "reset", "unsubscribe", "check-in"]);

/**
 * Which tab the auth screen opens on (2026-09-24): `#/auth` is signing in,
 * `#/auth?mode=sign-up` is creating an account, so a door elsewhere (the
 * guest's "create an account.") can land on the right one.
 */
export type AuthMode = "sign-in" | "sign-up" | "forgot";

/**
 * `#/auth?mode=forgot` (2026-10-22) opens the card on "password reset"'s
 * form, where the reset page sends someone whose link has expired.
 */
export function authModeFromHash(hash: string): AuthMode {
  const query = hash.split("?")[1] ?? "";
  const mode = new URLSearchParams(query).get("mode");
  return mode === "sign-up" || mode === "forgot" ? mode : "sign-in";
}

export function hashForAuth(mode: AuthMode): string {
  return mode === "sign-in" ? hashForRoute("auth") : `${hashForRoute("auth")}?mode=${mode}`;
}

/** The finished engagement a casebook shows, from `#/book?session=<id>`; null when none is named. */
export function bookSessionFromHash(hash: string): string | null {
  const query = hash.split("?")[1] ?? "";
  const id = new URLSearchParams(query).get("session");
  return id === null || id.length === 0 ? null : id;
}

export function hashForBook(sessionId: string): string {
  return `${hashForRoute("book")}?session=${encodeURIComponent(sessionId)}`;
}

/**
 * The signed token a mail link carries (`#/unsubscribe?token=…`,
 * `#/check-in?token=…`), or null when the hash names none. The token is
 * opaque here: the server verifies it (packages/mail's `verifyMailLink`), and
 * the page only hands it back in the one request it makes.
 */
export function mailTokenFromHash(hash: string): string | null {
  const query = hash.split("?")[1] ?? "";
  const token = new URLSearchParams(query).get("token");
  return token === null || token.length === 0 ? null : token;
}

export function hashForUnsubscribe(token: string): string {
  return `${hashForRoute("unsubscribe")}?token=${encodeURIComponent(token)}`;
}

export function hashForCheckIn(token: string): string {
  return `${hashForRoute("check-in")}?token=${encodeURIComponent(token)}`;
}

/**
 * The account emails' links (2026-10-22), as the api writes them after the
 * web origin: the verification JWT and the reset token, each read back by
 * `mailTokenFromHash` and handed only to the one request its page makes.
 */
export function hashForVerify(token: string): string {
  return `${hashForRoute("verify")}?token=${encodeURIComponent(token)}`;
}

export function hashForReset(token: string): string {
  return `${hashForRoute("reset")}?token=${encodeURIComponent(token)}`;
}

/**
 * The address to put in place of an account email's link once the server
 * has answered in a way that spends or settles its token (the pre-release
 * review, round two, 2026-10-22): `#/verify` or `#/reset`, bare, so the
 * tab's history no longer offers a link that confirmed an account, or set a
 * password, to whoever presses Back on a shared browser. Until then the link
 * stays as it came: "Open in Safari" from a mail app's own browser, a copied
 * address and a reload all carry the address, so it must still work there.
 * Null unless the address is the account link carrying `token` itself: a
 * new link opened in the tab while the answer was on its way is kept, and
 * every other address, the mail pages' included, is left alone.
 */
export function hashWithoutAccountToken(hash: string, token: string): string | null {
  const route = routeFromHash(hash);
  if (route !== "verify" && route !== "reset") return null;
  return mailTokenFromHash(hash) === token ? hashForRoute(route) : null;
}

/**
 * THE ARRIVAL BOOK (2026-10-11): `#/book?arrival=1` is the record's first
 * book, how the learner and the coach met. It shares the `book` route with
 * the casebooks, which name a session instead, so one hash can only ever
 * open one of the two.
 */
export function bookArrivalFromHash(hash: string): boolean {
  const query = hash.split("?")[1] ?? "";
  return new URLSearchParams(query).get("arrival") === "1";
}

export function hashForArrivalBook(): string {
  return `${hashForRoute("book")}?arrival=1`;
}
